import { Course, Status, SurveyAnswer } from '@modules/learning/types/learning';
import safeLocalStorage from '@modules/utilities/utils/safeLocalStorage';

const STORAGE_KEYS = {
  SURVEY_ANSWERS: 'learning_survey_answers',
  COURSES: 'courses',
  LEARNING_PROGRESS: 'learning_progress',
  ENABLE_COURSES: 'enable_courses',
} as const;

const getStorageData = <T>(key: string, defaultValue: T): T => {
  try {
    const item = safeLocalStorage.getItem(key);
    return item ? JSON.parse(item) : defaultValue;
  } catch (error) {
    console.warn('Failed to get localStorage key:', key, error);
    return defaultValue;
  }
};

const setStorageData = <T>(key: string, data: T): void => {
  try {
    safeLocalStorage.setItem(key, JSON.stringify(data));
  } catch (error) {
    console.warn('Failed to save to localStorage:', key, error);
  }
};

export const getLocalSurveyAnswers = (): SurveyAnswer | null => {
  return getStorageData(STORAGE_KEYS.SURVEY_ANSWERS, null);
};

export const setLocalSurveyAnswers = (answers: SurveyAnswer): void => {
  setStorageData(STORAGE_KEYS.SURVEY_ANSWERS, answers);
};

export const clearLocalSurveyAnswers = (): void => {
  safeLocalStorage.removeItem(STORAGE_KEYS.SURVEY_ANSWERS);
};

export const getLocalCourses = (): Course[] | null => {
  return getStorageData(STORAGE_KEYS.COURSES, []);
};

export const setLocalCourse = (path: Course): void => {
  setStorageData(STORAGE_KEYS.COURSES, path);
};

export const getLocalLearningProgress = (): Record<string, Status> => {
  return getStorageData(STORAGE_KEYS.LEARNING_PROGRESS, {});
};

export const setLocalLearningProgress = (progress: Record<string, Status>): void => {
  setStorageData(STORAGE_KEYS.LEARNING_PROGRESS, progress);
};

export const updateLocalLessonProgress = (lessonPath: string, status: Status): void => {
  const progress = getLocalLearningProgress();
  progress[lessonPath] = status;
  setLocalLearningProgress(progress);
};

const ENABLE_COURSES_EXPIRATION_TIME = 14 * 24 * 60 * 60 * 1000;

type UserCourseSetting = {
  enabled: boolean;
  expiresAt: number;
};

type EnableCoursesSettings = {
  [userId: string]: UserCourseSetting;
};

export const getLocalEnableCourses = (userId?: string): boolean => {
  if (!userId) return false;

  const settings = getStorageData<EnableCoursesSettings>(STORAGE_KEYS.ENABLE_COURSES, {});
  const userSetting = settings[userId];

  if (!userSetting || Date.now() > userSetting.expiresAt) {
    if (userSetting) {
      delete settings[userId];
      setStorageData(STORAGE_KEYS.ENABLE_COURSES, settings);
    }
    return false;
  }

  return userSetting.enabled;
};

export const setLocalEnableCourses = (enabled: boolean, userId?: string): void => {
  if (!userId) return;

  const settings = getStorageData<EnableCoursesSettings>(STORAGE_KEYS.ENABLE_COURSES, {});
  settings[userId] = {
    enabled,
    expiresAt: Date.now() + ENABLE_COURSES_EXPIRATION_TIME,
  };

  setStorageData(STORAGE_KEYS.ENABLE_COURSES, settings);
};

export const clearAllLocalLearningData = (): void => {
  safeLocalStorage.removeItem(STORAGE_KEYS.SURVEY_ANSWERS);
  safeLocalStorage.removeItem(STORAGE_KEYS.COURSES);
  safeLocalStorage.removeItem(STORAGE_KEYS.LEARNING_PROGRESS);
  safeLocalStorage.removeItem(STORAGE_KEYS.ENABLE_COURSES);
};
